/** Вкладка «План»: план/факт месяца по площадкам × юрлицам, отставание от плана на дату, прогноз
 *  на конец месяца (режим — переключатель «Прогноз», `fc` в адресе), нужный темп; редактор планов
 *  (право `mp_plans_edit`). Обе площадки сразу — переключатель площадки, период и юрлицо страницы
 *  здесь не действуют. Расчёты — planCalc.ts и planForecast.ts, источник правды —
 *  docs/ozon-analytics.md «Экран → План». */
import React, { useState } from "react";

import { isMpError, type PlanFact, type PlanFactMp, type PlanMetric } from "@/api/mpPlans";
import { cn } from "@/lib/utils";
import { cogsNullTitle } from "./cogs";
import { CogsCoverageView } from "./CogsCoverage";
import { dayMonth, pct, pctSigned, rub, rubSigned } from "./format";
import {
  allTotal,
  forecastTone,
  mpBlock,
  tableStateOf,
  type MonthState,
  type MpPlanBlock,
  type PlanCell,
} from "./planCalc";
import { MonthPicker } from "./MonthPicker";
import { PlanEditor } from "./PlanEditor";
import { FORECAST_MODES, forecastModeLabel, type ForecastMode, type Forecasts } from "./planForecast";
import { usePlanFact } from "./queries";
import { Loading, Seg } from "./shared";
import { apiErrorText, tdNum, thClass } from "./ui";
import { useMpPlansEdit } from "./useMpPlansEdit";

const RED = "text-red-600 dark:text-red-400";
const GREEN = "text-emerald-700 dark:text-emerald-400";
const AMBER = "text-amber-700 dark:text-amber-400";
const TONE: Record<"good" | "warn" | "bad", string> = { good: GREEN, warn: AMBER, bad: RED };
const BADGE =
  "ml-2 inline-block rounded-full border border-amber-300 bg-amber-50 px-1.5 text-[11px] font-normal text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300";

const money = (v: number | null): string => (v === null ? "—" : rub(v));
/** Факт: null при прошедших днях — «нет данных» (не покрыт себестоимостью), будущий — «—». */
const NO_DATA = "нет данных";
const factText = (c: PlanCell): string => (c.fact === null && c.factMissing ? NO_DATA : money(c.fact));
const moneySigned = (v: number | null): string => (v === null ? "—" : rubSigned(v));
const signTone = (v: number | null): string => (v === null ? "" : v < -0.5 ? RED : v > 0.5 ? GREEN : "");

/** Подсказка у прогноза: все три режима (недоступный — с причиной), выбранный отмечен. */
function forecastTitle(f: Forecasts, mode: ForecastMode): string {
  const lines = FORECAST_MODES.map((m) => {
    const x = f[m.id];
    const v = x.value === null ? `недоступен — ${x.reason ?? ""}` : rub(x.value);
    return `${m.id === mode ? "▸ " : ""}${m.label}: ${v}`;
  });
  return ["Прогноз на конец месяца", ...lines].join("\n");
}

const FC_HINT: Record<ForecastMode, string> = {
  avg: "по среднему: факт + факт / прошедшие дни × оставшиеся дни",
  last7: "по 7 дням: факт + среднее за 7 последних полных дней × оставшиеся дни",
  trend: "по тренду: факт + прямая МНК по 28 последним полным дням, продолженная на оставшиеся дни",
};

interface Col {
  key: string;
  title: string;
  hint?: string;
  render: (c: PlanCell) => { text: string; cls?: string; sub?: string };
}

const C_PLAN: Col = { key: "plan", title: "План", render: (c) => ({ text: money(c.plan) }) };
/** у текущего месяца рядом «План на дату» — столбец плана подписан явно */
const C_PLAN_MONTH: Col = { ...C_PLAN, title: "План на месяц" };
const C_FACT: Col = {
  key: "fact",
  title: "Факт",
  render: (c) => ({ text: factText(c), cls: c.fact === null && c.factMissing ? "text-muted-foreground" : undefined }),
};
const C_DONE: Col = {
  key: "done",
  title: "% выполнения",
  hint: "факт / план на месяц",
  render: (c) => ({ text: pct(c.donePct) }),
};
const C_DEV: Col = {
  key: "dev",
  title: "Отклонение",
  hint: "факт − план",
  render: (c) => ({ text: moneySigned(c.deviation), cls: signTone(c.deviation) }),
};
const C_PTD: Col = {
  key: "ptd",
  title: "План на дату",
  hint: "план × прошедшие дни / дней в месяце",
  render: (c) => ({ text: money(c.planToDate) }),
};
const C_LAG: Col = {
  key: "lag",
  title: "Отставание",
  hint: "факт − план на дату (₽); ниже — % от плана на дату",
  render: (c) => ({ text: moneySigned(c.lag), cls: signTone(c.lag), sub: pctSigned(c.lagPct) }),
};
const C_FC: Col = {
  key: "fc",
  title: "Прогноз",
  // подсказка — по режиму (`colsOf`)
  render: (c) => {
    const t = forecastTone(c);
    // без плана подстрока «— плана» ничего не говорит — только прогноз
    const sub = c.forecastPct === null ? undefined : `${pct(c.forecastPct)} плана`;
    return { text: money(c.forecast), cls: t ? TONE[t] : "", sub };
  },
};
const C_CUR: Col = {
  key: "cur",
  title: "Сейчас в день",
  // подсказка — по режиму (`colsOf`)
  render: (c) => ({ text: money(c.curPerDay) }),
};
const C_NEED: Col = {
  key: "need",
  title: "Нужно в день",
  hint: "(план на месяц − факт) / оставшиеся дни",
  render: (c) => ({ text: money(c.needPerDay) }),
};

const COLS: Record<MonthState, Col[]> = {
  future: [C_PLAN],
  closed: [C_PLAN, C_FACT, C_DONE, C_DEV],
  // порядок — решение владельца 22.10.2026
  current: [C_PTD, C_FACT, C_LAG, C_PLAN_MONTH, C_DONE, C_FC, C_CUR, C_NEED],
};

const CUR_HINT: Record<ForecastMode, string> = {
  avg: "по среднему: факт / прошедшие дни",
  last7: "по 7 дням: среднее за 7 последних полных дней",
  trend: "по тренду: значение прямой МНК на следующий день",
};

function colsOf(state: MonthState, mode: ForecastMode): Col[] {
  return COLS[state].map((c) => {
    if (c.key === "fc") {
      return { ...c, hint: `${FC_HINT[mode]}; ниже — к плану: ≥ 100% — зелёный, ≥ 90% — жёлтый, ниже — красный` };
    }
    if (c.key === "cur") return { ...c, hint: `темп ${CUR_HINT[mode]}` };
    return c;
  });
}

const NOTE =
  "Маржа — выручка − расходы (все удержания МП + себестоимость: производство с НДС + склад производства), как в Сводке; месяц не покрыт себестоимостью — «нет данных». Прогноз на конец месяца: по среднему — факт + средний день месяца × оставшиеся дни; по 7 дням — факт + средний день 7 последних полных дней × оставшиеся дни; по тренду — факт + прямая по 28 последним полным дням, продолженная на оставшиеся дни. Итог площадки — по её дневному ряду; мало истории или дни без себестоимости в окне — режим недоступен (причина — в подсказке у прогноза).";

export function PlanTab({
  month,
  today,
  onMonth,
  forecastMode,
  onForecastMode,
}: {
  month: string;
  today: string;
  onMonth: (m: string) => void;
  forecastMode: ForecastMode;
  onForecastMode: (m: ForecastMode) => void;
}): React.ReactElement {
  const canEdit = useMpPlansEdit();
  const [editing, setEditing] = useState(false);
  const q = usePlanFact(month);
  return (
    <div className="space-y-4" data-testid="plan-tab">
      <div className="flex flex-wrap items-center gap-3">
        <MonthPicker month={month} today={today} onMonth={onMonth} label="Месяц плана" testId="plan-month" />
        {q.data && hasCurrent(q.data) && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Прогноз:</span>
            <Seg label="Прогноз" value={forecastMode} options={FORECAST_MODES} onChange={onForecastMode} />
          </div>
        )}
        {canEdit && (
          <button
            type="button"
            aria-pressed={editing}
            onClick={() => setEditing((v) => !v)}
            className={cn(
              "h-8 rounded-md border px-3 text-xs font-medium",
              editing ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent",
            )}
          >
            {editing ? "Закрыть редактор" : "Изменить планы"}
          </button>
        )}
      </div>
      {canEdit && editing && <PlanEditor key={month} month={month} />}
      {q.isLoading && <Loading text="Считаю план/факт по витринам…" />}
      {q.isError && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
          Не удалось получить план/факт. {apiErrorText(q.error, "площадки")}
        </div>
      )}
      {q.data && <PlanFactView data={q.data} mode={forecastMode} />}
    </div>
  );
}

/** Есть площадка с текущим месяцем (0 < e < D) — только тогда есть прогноз и переключатель. */
function hasCurrent(data: PlanFact): boolean {
  return data.mps.some((m) => !isMpError(m) && m.days_elapsed > 0 && m.days_elapsed < data.days_in_month);
}

interface MetricView {
  metric: PlanMetric;
  blocks: Map<PlanFactMp, MpPlanBlock>;
  all: PlanCell;
  state: MonthState;
}

function buildViews(data: PlanFact, mode: ForecastMode): MetricView[] {
  const ok = data.mps.filter((m) => !isMpError(m));
  return data.metrics.map((metric) => {
    const blocks = new Map<PlanFactMp, MpPlanBlock>();
    for (const m of ok) {
      if (!isMpError(m)) blocks.set(m, mpBlock(m, metric.code, data.days_in_month, data.month, mode));
    }
    const list = [...blocks.values()];
    return {
      metric,
      blocks,
      all: allTotal(list, mode),
      // все площадки с ошибкой — показывать нечего, кроме плана
      state: tableStateOf(list.map((b) => b.state)),
    };
  });
}

function PlanFactView({ data, mode }: { data: PlanFact; mode: ForecastMode }): React.ReactElement {
  const views = buildViews(data, mode);
  const failed = data.mps.filter(isMpError).map((m) => m.label);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="plan-kpis">
        {views.map((v) => (
          <Kpis key={v.metric.code} view={v} failed={failed} mode={mode} />
        ))}
      </div>
      {views.map((v) => (
        <MetricTable key={v.metric.code} view={v} data={data} failed={failed} mode={mode} />
      ))}
      <p className="text-xs text-muted-foreground">{NOTE}</p>
    </div>
  );
}

function allLabel(failed: string[]): string {
  return failed.length ? `Все площадки (без ${failed.join(", ")} — ошибка витрины)` : "Все площадки";
}

function Kpis({
  view,
  failed,
  mode,
}: {
  view: MetricView;
  failed: string[];
  mode: ForecastMode;
}): React.ReactElement {
  const c = view.all;
  const items: { title: string; text: string; sub?: string; cls?: string; hint?: string }[] = [];
  if (view.state === "future") {
    items.push({ title: "План", text: money(c.plan) });
  } else {
    items.push({ title: "Факт", text: factText(c) }, { title: "План", text: money(c.plan) });
    if (view.state === "closed") {
      items.push(
        { title: "% выполнения", text: pct(c.donePct) },
        { title: "Отклонение", text: moneySigned(c.deviation), cls: signTone(c.deviation) },
      );
    } else {
      const t = forecastTone(c);
      items.push(
        {
          title: `Прогноз ${forecastModeLabel(mode)}`,
          text: money(c.forecast),
          sub: c.forecastPct === null ? undefined : `${pct(c.forecastPct)} плана`,
          cls: t ? TONE[t] : "",
          hint: forecastTitle(c.forecasts, mode),
        },
        { title: "Отставание", text: moneySigned(c.lag), sub: pctSigned(c.lagPct), cls: signTone(c.lag) },
      );
    }
  }
  return (
    <div className="rounded-md border bg-card p-3" data-testid="plan-kpi">
      <div className="text-xs font-medium">
        {view.metric.label} · {allLabel(failed)}
        <UnplannedBadge cell={c} />
        <MissingBadge cell={c} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
        {items.map((i) => (
          <div key={i.title} title={i.hint} data-testid={i.hint ? "plan-kpi-forecast" : undefined}>
            <div className="text-[11px] text-muted-foreground">{i.title}</div>
            <div className={cn("text-base font-semibold tabular-nums", i.cls)}>{i.text}</div>
            {i.sub && <div className={cn("text-[11px] tabular-nums", i.cls)}>{i.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function UnplannedBadge({ cell }: { cell: PlanCell }): React.ReactElement | null {
  if (cell.unplannedFact === 0) return null;
  return (
    <span
      className={BADGE}
      data-testid="unplanned-fact"
      title={`Факт без плана: ${rub(cell.unplannedFact)} (${cell.unplannedWho.join(", ")}) — входит в «Факт» и «Прогноз» (и в прогноз к плану — прогноз итога по всему ряду площадки), но не в % выполнения и отставание`}
    >
      часть факта без плана
    </span>
  );
}

/** Итог, где у части участников факта нет («нет данных» — месяц не покрыт себестоимостью): они не
 *  входят ни в факт, ни в сравнение с планом. Весь итог без факта — «нет данных» в ячейке, без значка. */
function MissingBadge({ cell }: { cell: PlanCell }): React.ReactElement | null {
  if (cell.factMissing || cell.missingWho.length === 0) return null;
  return (
    <span
      className={BADGE}
      data-testid="missing-fact"
      title={`Нет данных: ${cell.missingWho.join(", ")} — не входит ни в «Факт», ни в сравнение с планом`}
    >
      часть факта — нет данных
    </span>
  );
}

function Cells({
  cols,
  cell,
  mode,
  missingTitle,
}: {
  cols: Col[];
  cell: PlanCell;
  mode: ForecastMode;
  /** подсказка к «нет данных» (почему факта нет) */
  missingTitle?: string;
}): React.ReactElement {
  return (
    <>
      {cols.map((col) => {
        const r = col.render(cell);
        const title =
          col.key === "fc"
            ? forecastTitle(cell.forecasts, mode)
            : col.key === "fact" && cell.factMissing
              ? missingTitle
              : undefined;
        return (
          <td key={col.key} className={cn(tdNum, r.cls)} data-col={col.key} title={title}>
            {r.text}
            {r.sub !== undefined && (
              <div className="text-[11px] font-normal leading-tight" data-sub>
                {r.sub}
              </div>
            )}
          </td>
        );
      })}
    </>
  );
}

function MetricTable({
  view,
  data,
  failed,
  mode,
}: {
  view: MetricView;
  data: PlanFact;
  failed: string[];
  mode: ForecastMode;
}): React.ReactElement {
  const cols = colsOf(view.state, mode);
  const span = cols.length + 1;
  return (
    <section className="space-y-1" data-testid={`plan-table-${view.metric.code}`}>
      <h2 className="text-sm font-semibold">{view.metric.label}</h2>
      <div className="overflow-x-auto">
        <table className="w-auto border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th className={cn(thClass, "text-left")}>Площадка / юрлицо</th>
              {cols.map((c) => (
                <th key={c.key} className={thClass} title={c.hint}>
                  {c.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.mps.map((m) => {
              if (isMpError(m)) {
                return (
                  <React.Fragment key={m.mp}>
                    <tr className="border-t bg-muted/40">
                      <td colSpan={span} className="px-2 py-1.5 font-semibold">
                        {m.label}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={span} className="px-2 py-1.5">
                        <div
                          role="alert"
                          data-testid={`plan-mp-error-${m.mp}`}
                          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
                        >
                          Витрина {m.label} недоступна: {m.error}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }
              const b = view.blocks.get(m)!;
              const metricError = m.metric_errors?.[view.metric.code];
              const missingTitle = view.metric.cogs ? cogsNullTitle(m.coverage?.cogs, m.label) : undefined;
              return (
                <React.Fragment key={m.mp}>
                  <tr className="border-t bg-muted/40">
                    <td colSpan={span} className="px-2 py-1.5">
                      <span className="font-semibold">{m.label}</span>
                      <span className="ml-2 text-xs text-muted-foreground" data-testid={`plan-mp-days-${m.mp}`}>
                        данные по {dayMonth(m.data_to)} · {m.days_elapsed} из {data.days_in_month} дн
                      </span>
                    </td>
                  </tr>
                  {metricError && (
                    <tr>
                      <td colSpan={span} className="px-2 py-1.5">
                        <div
                          role="alert"
                          data-testid={`plan-metric-error-${m.mp}`}
                          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
                        >
                          {view.metric.label}: {metricError}
                        </div>
                      </td>
                    </tr>
                  )}
                  {view.metric.cogs && m.coverage && (
                    <tr>
                      <td colSpan={span} className="px-2 py-1 text-xs" data-testid={`plan-coverage-${m.mp}`}>
                        <CogsCoverageView
                          coverage={m.coverage}
                          mpId={m.mp}
                          dateFrom={m.coverage.date_from}
                          dateTo={m.coverage.date_to}
                          legal={null}
                        />
                      </td>
                    </tr>
                  )}
                  {b.rows.map((r) => (
                    <tr key={r.legal ?? "\u0000"} className="border-t border-border/50" data-testid="plan-legal-row">
                      <td className="px-2 py-1 pl-5 whitespace-nowrap">
                        {r.label}
                        {r.dataLag && (
                          <span
                            className={BADGE}
                            data-testid="legal-data-lag"
                            title="отставание считается по дням площадки — у юрлица фактов меньше дней; прогноз — от данных юрлица по его дату"
                          >
                            {r.dataLag}
                          </span>
                        )}
                      </td>
                      <Cells cols={cols} cell={r.cell} mode={mode} missingTitle={missingTitle} />
                    </tr>
                  ))}
                  <tr className="border-t font-medium" data-testid={`plan-mp-total-${m.mp}`}>
                    <td className="px-2 py-1 whitespace-nowrap">
                      Итого {m.label}
                      <UnplannedBadge cell={b.total} />
                      <MissingBadge cell={b.total} />
                    </td>
                    <Cells cols={cols} cell={b.total} mode={mode} missingTitle={missingTitle} />
                  </tr>
                </React.Fragment>
              );
            })}
            <tr className="border-t-2 font-semibold" data-testid="plan-all-total">
              <td className="px-2 py-1.5 whitespace-nowrap">
                {allLabel(failed)}
                <UnplannedBadge cell={view.all} />
                <MissingBadge cell={view.all} />
              </td>
              <Cells cols={cols} cell={view.all} mode={mode} />
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
